import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Contact } from "@/types/contacts";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface EditContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact | null;
  onUpdate: (contact: Contact) => void;
  onDelete: (contact: Contact) => void;
}

interface Entity {
  id: number;
  name: string;
  type: string;
  region: string;
  state: string;
}

export function EditContactDialog({
  open,
  onOpenChange,
  contact,
  onUpdate,
  onDelete,
}: EditContactDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    position: "",
    cellPhone: "",
    phone: "",
    email: "",
    website: "",
    city: "",
    state: "",
  });

  // Carregar dados do contato quando abrir
  useEffect(() => {
    if (open && contact) {
      setFormData({
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        position: contact.position || "",
        cellPhone: contact.cellPhone || "",
        phone: contact.phone || "",
        email: contact.email || "",
        website: contact.website || "",
        city: contact.city || "",
        state: contact.state || "",
      });
      fetchEntities();
    }
  }, [open, contact]);

  const fetchEntities = async () => {
    try {
      const { data, error } = await supabase
        .from("entities")
        .select("id, name, type, region, state")
        .order("name");

      if (error) throw error;
      setEntities(data || []);

      // Encontrar entidade correspondente
      if (contact) {
        const entity = data?.find(e => e.name === contact.company);
        if (entity) {
          setSelectedEntityId(entity.id);
        }
      }
    } catch (error) {
      console.error("Error fetching entities:", error);
    }
  };

  const selectedEntity = entities.find(e => e.id === selectedEntityId);

  const filteredEntities = entities.filter(entity => {
    if (!searchQuery) return true;
    return entity.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleSubmit = async () => {
    if (!contact || !selectedEntityId) return;

    // Validação básica
    if (!formData.firstName.trim() || !formData.email.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Preencha Nome e E-mail.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const entity = entities.find(e => e.id === selectedEntityId);
      if (!entity) {
        toast({
          title: "Erro",
          description: "Entidade não encontrada.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const updatedContact: Contact = {
        ...contact,
        ...formData,
        company: entity.name,
        entityType: entity.type as "restaurant" | "operator" | "consolidator",
        region: entity.region || "",
      };

      onUpdate(updatedContact);
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating contact:", error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o contato.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (contact) {
      onDelete(contact);
      onOpenChange(false);
    }
  };

  if (!contact) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Contato</DialogTitle>
          <DialogDescription>
            Atualize os dados do contato. Campos com * são obrigatórios.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="space-y-2 col-span-2">
            <Label htmlFor="entity">Empresa *</Label>
            <Select
              value={selectedEntityId?.toString()}
              onValueChange={(value) => setSelectedEntityId(Number(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma empresa" />
              </SelectTrigger>
              <SelectContent>
                <div className="p-2">
                  <Input
                    placeholder="Buscar empresa..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mb-2"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {filteredEntities.length > 0 ? (
                  filteredEntities.map((entity) => (
                    <SelectItem key={entity.id} value={entity.id.toString()}>
                      <div className="flex flex-col">
                        <span className="font-medium">{entity.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {entity.state} • {entity.type}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground">
                    Nenhuma empresa encontrada
                  </div>
                )}
              </SelectContent>
            </Select>
            {selectedEntity && (
              <div className="text-sm text-muted-foreground mt-1">
                {selectedEntity.type === "restaurant" && "Restaurante"}
                {selectedEntity.type === "operator" && "Operadora"}
                {selectedEntity.type === "consolidator" && "Consolidadora"}
                {" • "}
                {selectedEntity.state}
                {selectedEntity.region && ` • ${selectedEntity.region}`}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="firstName">Nome *</Label>
            <Input
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Nome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Sobrenome</Label>
            <Input
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Sobrenome"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mail *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@exemplo.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="position">Cargo</Label>
            <Input
              id="position"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              placeholder="Cargo"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cellPhone">Celular</Label>
            <Input
              id="cellPhone"
              value={formData.cellPhone}
              onChange={(e) => setFormData({ ...formData, cellPhone: e.target.value })}
              placeholder="(00) 00000-0000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Telefone Fixo</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="(00) 0000-0000"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">Cidade</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              placeholder="Cidade"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">Estado</Label>
            <Input
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              placeholder="Estado"
            />
          </div>

          <div className="space-y-2 col-span-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              placeholder="www.exemplo.com"
            />
          </div>
        </div>

        <DialogFooter className="flex justify-between items-center">
          <Button
            variant="destructive"
            onClick={handleDeleteClick}
            disabled={loading}
            className="mr-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Deletar
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Alterações
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
